/* eslint-disable max-depth */
/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { FtoReportImportPatentsRepository, ImportPatentsOptions, ImportPatentsStatus } from '../import-patents-repository';
import { Logger } from '@patsnap/tahiti-core';

import { FtoReportImportPatentsApi, type GetImportProgressResponse, type StartImportPatentsResponse } from '../../apis/import-patents-api';

export const createInitialImportPatentsStatus = (base: Partial<ImportPatentsStatus> = {}): ImportPatentsStatus => {
    return {
        asyncId: '',
        taskId: '',
        folderId: '',
        spaceId: '',
        folderPath: '',
        status: '',
        totalPatents: 0,
        estimatedMinutes: 0,
        batchSize: 0,
        processedPatents: 0,
        completedPatents: 0,
        failedPatents: 0,
        failedPatentIds: [],
        progress: 0,
        newCompletedPatentIds: [],
        newResults: [],
        estimatedRemainingMinutes: 0,
        currentTimestamp: 0,
        ...base,
    };
};

@injectable()
export class FtoReportImportPatentsRepositoryImpl implements FtoReportImportPatentsRepository {
    constructor(
        @inject(FtoReportImportPatentsApi)
        private readonly _importPatentsApi: FtoReportImportPatentsApi,
        @inject(Logger)
        private readonly _logger: Logger,
    ) { }

    async importPatents(taskId: string, options: ImportPatentsOptions = {}) {
        const {
            folderId,
            signal,
            onImportPatentsStart,
            onImportPatentsProgress,
            onImportPatentsComplete,
        } = options;

        const aborted = signal?.aborted || false;
        let status = { taskId } as ImportPatentsStatus;
        const response = await this._importPatentsApi.startImportPatents({
            task_id: taskId,
            folder_id: folderId,
            batch_size: options.batchSize,
        }, signal);
        if (response?.async_id) {
            const startStatus = this._convertStartImportPatentsResponse(response);
            status = {
                ...status,
                ...startStatus,
            };
            await onImportPatentsStart?.(status);
            this._logger.info('开始导入专利', status.asyncId);

            const syncStatus = async () => {
                try {
                    const response = await this._importPatentsApi.getImportProgress({
                        async_id: status.asyncId,
                    }, signal);
                    status = {
                        ...status,
                        ...this._convertGetImportProgressResponse(response),
                    };
                    await onImportPatentsProgress?.(status);
                    if (status.status === 'PROCESSING' && !aborted) {
                        await new Promise(resolve => setTimeout(resolve, 5000));
                    }
                } catch (error) {
                    this._logger.error('获取导入专利进度失败', error);
                }
            };

            (async () => {
                while (status.status === 'PROCESSING' && !aborted) {
                    await syncStatus();
                }

                if (aborted) {
                    this._logger.info('导入专利已终止', status.asyncId);
                    throw signal?.reason;
                } else {
                    await onImportPatentsComplete?.(status);
                    this._logger.info('导入专利完成', status.asyncId);
                }
                this._logger.info(`导入专利总数${status.totalPatents}，完成 ${status.completedPatents}，失败${status.failedPatents}`);
            })();

        } else {
            this._logger.info('没有进行中的导入任务', taskId);
        }
        return status;
    }

    private _convertStartImportPatentsResponse(response: Partial<StartImportPatentsResponse> = {}): Partial<ImportPatentsStatus> {
        return {
            asyncId: response.async_id,
            status: response.status,
            totalPatents: response.total_patents,
            estimatedMinutes: response.estimated_minutes,
            batchSize: response.batch_size,
        };
    }

    private _convertGetImportProgressResponse(response: Partial<GetImportProgressResponse> = {}): Partial<ImportPatentsStatus> {
        return {
            asyncId: response.async_id,
            status: response.status,
            processedPatents: response.processed_patents,
            completedPatents: response.completed_patents,
            failedPatents: response.failed_patents,
            progress: response.progress ?? 0,
            failedPatentIds: response.failed_patent_ids || [],
            newCompletedPatentIds: response.new_completed_patent_ids || [],
            newResults: response.new_results || [],
            currentTimestamp: response.current_timestamp,
            estimatedRemainingMinutes: response.estimated_remaining_minutes,
            estimatedMinutes: response.estimated_remaining_minutes,
        };
    }
}
